// utils/client-country-detection.js
import axios from "axios"

/**
 * Detects the user's country from their browser
 * This is a client-side fallback for when server-side detection fails
 */
export async function detectClientCountry() {
  try {
    console.log("Starting client-side country detection...")

    // Create an axios instance that doesn't send credentials
    const axiosInstance = axios.create({
      withCredentials: false,
      headers: {
        "Content-Type": "application/json",
      },
    })

    // First try using the server-side proxy for IP detection
    // This avoids CORS issues completely
    try {
      const SERVER_URL = process.env.REACT_APP_SERVER_URL
      console.log("Using server proxy for IP detection")

      const response = await axiosInstance.get(`${SERVER_URL}/api/geo/detect-country`)
      console.log("Server IP detection response:", response.data)

      if (response.data && response.data.country && response.data.country !== "Unknown") {
        console.log("Country detected via server IP detection:", response.data.country)
        return response.data.country
      }
    } catch (serverError) {
      console.error("Server IP detection failed:", serverError)
    }

    // Try using the browser's Geolocation API as a fallback
    if (navigator.geolocation) {
      try {
        console.log("Browser supports geolocation, requesting permission...")
        // This will prompt the user for permission
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 10000, // Increased timeout
            maximumAge: 24 * 60 * 60 * 1000, // 24 hours
          })
        })

        console.log("Geolocation permission granted, coordinates:", position.coords)
        const { latitude, longitude } = position.coords

        // Use our server-side proxy instead of calling the external API directly
        // This avoids CORS issues
        const SERVER_URL = process.env.REACT_APP_SERVER_URL
        console.log(`Using server proxy for coordinates: ${latitude}, ${longitude}`)

        try {
          const response = await axiosInstance.get(
            `${SERVER_URL}/api/geo/reverse-geocode?lat=${latitude}&lon=${longitude}`,
          )
          console.log("Server proxy geocoding response:", response.data)

          if (response.data && response.data.country && response.data.country !== "Unknown") {
            console.log("Country detected via coordinates:", response.data.country)
            return response.data.country
          }
        } catch (proxyError) {
          console.error("Server proxy geocoding error:", proxyError)
        }
      } catch (geoError) {
        console.log("Geolocation permission denied or error:", geoError)
      }
    } else {
      console.log("Browser does not support geolocation")
    }

    // If all else fails, use a hardcoded fallback based on language
    try {
      const language = navigator.language || navigator.userLanguage
      console.log("Trying language-based fallback:", language)

      // Simple mapping of common language codes to countries
      const languageMap = {
        "en-US": "United States",
        "en-GB": "United Kingdom",
        "en-CA": "Canada",
        "en-AU": "Australia",
        fr: "France",
        "fr-CA": "Canada",
        es: "Spain",
        "es-MX": "Mexico",
        de: "Germany",
        it: "Italy",
        ru: "Russia",
        zh: "China",
        ja: "Japan",
        ar: "Saudi Arabia",
        hi: "India",
        pt: "Portugal",
        "pt-BR": "Brazil",
        nl: "Netherlands",
        tr: "Turkey",
        pl: "Poland",
      }

      if (language && languageMap[language]) {
        console.log("Country detected via language:", languageMap[language])
        return languageMap[language]
      } else if (language && language.includes("-")) {
        // Try the base language
        const baseLanguage = language.split("-")[0]
        if (languageMap[baseLanguage]) {
          console.log("Country detected via base language:", languageMap[baseLanguage])
          return languageMap[baseLanguage]
        }
      }
    } catch (langError) {
      console.error("Language detection failed:", langError)
    }

    console.log("All country detection methods failed")
    return "Unknown" // Return "Unknown" instead of null
  } catch (error) {
    console.error("Error in detectClientCountry:", error)
    return "Unknown" // Return "Unknown" instead of null
  }
}

/**
 * Updates the user's country in the database
 */
export async function updateUserCountry(telegramId, country) {
  try {
    const SERVER_URL = process.env.REACT_APP_SERVER_URL
    console.log(`Updating country for user ${telegramId} to ${country}`)

    // Create an axios instance that doesn't send credentials
    const axiosInstance = axios.create({
      withCredentials: false,
      headers: {
        "Content-Type": "application/json",
      },
    })

    const response = await axiosInstance.put(`${SERVER_URL}/api/telegram/user/update`, {
      telegramId,
      updates: { country },
    })

    console.log("Country update response:", response.data)

    if (response.data && response.data.success) {
      console.log("Country update successful")
      return true
    } else {
      console.error("Country update failed:", response.data)
      return false
    }
  } catch (error) {
    console.error("Error updating user country:", error)
    return false
  }
}

